import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useTransactionMutations } from '../components/transactions/hooks/useTransactionMutations';
import useDocumentFocus from '../hooks/useDocumentFocus';
import { RecalculateData } from '../services/transaction.types';
import { QUERY_KEYS } from '../utils/queryKeys';
import { useAuth } from './AuthContext';

interface RecalculationStatusContextProps {
    status: RecalculateData | null;
}

const RecalculationStatusContext = createContext<RecalculationStatusContextProps | undefined>(undefined);

export const RecalculationStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const [status, setStatus] = useState<RecalculateData | null>(null);
    const { getRecalculationInfo, startRecalculation } = useTransactionMutations();
    const isFocused = useDocumentFocus();
    const { currentCtUser } = useAuth();

    useQuery({
        queryKey: [QUERY_KEYS.RECALCULATION],
        queryFn: async () => {
            try {
                getRecalculationInfo({
                    onSuccess: (data: RecalculateData) => {
                        if (status?.run && !data.run) {
                            // Recalculation just finished
                            QUERY_KEYS.dataQueriesWithTransactions().forEach((key) => {
                                queryClient.invalidateQueries({ queryKey: [key] });
                            });
                        }

                        setStatus(data);

                        if (!data.run && data.new) {
                            // Should be running but isn't, start it
                            startRecalculation();
                        }
                    },
                });
            } catch (error) {
                console.error('Failed to check recalculation status:', error);
            }
            return null;
        },
        enabled: !!currentCtUser,
    });

    useQuery({
        queryKey: ['recalculateInfoInterval'],
        queryFn: async () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
            return null;
        },
        refetchInterval: 30000,
        enabled: ((isFocused && status?.run) || status === null) && !!currentCtUser,
    });

    const value = {
        status
    };

    return (
        <RecalculationStatusContext.Provider value={value}>
            {children}
        </RecalculationStatusContext.Provider>
    );
};

export const useRecalculation = (): RecalculationStatusContextProps => {
    const context = useContext(RecalculationStatusContext);
    if (context === undefined) {
        throw new Error('useRecalculation must be used within a RecalculationStatusProvider');
    }
    return context;
};
